/**
 * The reader's light/dark choice (light by default since Terrazzo, 25/10/2026 — docs/13-tasks/01-ui-chrome.md#67; docs/07-gui-proposal §2 chose dark). Pure verdicts here;
 * the hook does the storage and the DOM (S1).
 */
export const THEMES = ['system', 'light', 'dark'] as const;
export type Theme = (typeof THEMES)[number];

export const THEME_STORAGE_KEY = 'thongketau.theme';
export const DEFAULT_THEME: Theme = 'light';

export function isTheme(value: unknown): value is Theme {
  return typeof value === 'string' && (THEMES as readonly string[]).includes(value);
}

/** What a stored value means — anything unreadable is the default, never a crash. */
export function themeFromStorage(stored: string | null): Theme {
  return isTheme(stored) ? stored : DEFAULT_THEME;
}

/**
 * The value of the `data-theme` attribute. `system` sets none: the page then follows
 * `prefers-color-scheme`, which is exactly what app.css declares.
 */
export function themeAttribute(theme: Theme): string | null {
  return theme === 'system' ? null : theme;
}

/**
 * The theme actually PAINTED, once `system` has been resolved against the OS. WebGL knows
 * nothing about CSS: the map has to be handed a concrete light/dark style file, so `system`
 * must be turned into one of the two here rather than left to the stylesheet
 * (docs/14-vessel-map/03-plan.md §4.4).
 */
export type ResolvedTheme = 'light' | 'dark';

export function resolveTheme(theme: Theme, prefersDark: boolean): ResolvedTheme {
  if (theme === 'system') return prefersDark ? 'dark' : 'light';
  return theme;
}

/** The next theme when the reader presses the toggle: light → dark → system → light. */
export function nextTheme(theme: Theme): Theme {
  if (theme === 'light') return 'dark';
  if (theme === 'dark') return 'system';
  return 'light';
}

export const THEME_LABELS: Readonly<Record<Theme, string>> = {
  system: 'Theo hệ thống',
  light: 'Sáng',
  dark: 'Tối',
};
